import {
  Box,
  Button,
  Center,
  Group,
  Modal,
  Notification,
  rem,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconEdit,
  IconPlus,
  IconSearch,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";
import styled from "styled-components";
import { ReactNode, isValidElement, useContext, useState } from "react";
import { AppContext } from "../../../Context";
import "../../../index.css";
import { IncomesItem, OutgoingItem } from "../../../backend/types-v2";
import { ItemForm2 } from "./ItemForm2";
import { FormValidateInput, UseFormReturnType } from "@mantine/form/lib/types";
import { Month } from "@js-joda/core";

const StyledTable = styled(Table)`
  // border: 1px solid;

  td,
  th {
    &#description {
      width: 200px;
    }

    &#cost {
      width: 200px;
    }

    &#date {
      width: 200px;
    }

    &#regularity {
      width: 200px;
    }

    &#buttons {
      div {
        display: flex;

        button:not(:nth-child(1)) {
          margin-left: 12px;
        }
      }
    }
  }
`;

export const FREQUENCIES = [
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "business-days",
  "weekdays",
  "specific-dates",
] as const;

export type Frequency = (typeof FREQUENCIES)[number];

export type FormData2 = {
  description: string;
  amount: number | null;
  scheduleType: "repeats" | "one-off" | null;
  interval: number | null;
  frequency: Frequency | null;
  weekday: string | null;

  yearlyRule: {
    month: Month;
    day: number;
    fromYear: number;
    toYear: number | null;
  }; // min 1 for 1st month/day

  oneOffDates: null | Array<Date | null>;
  monthlyRule:
    | null
    | { type: "same-day"; day: string }
    | { type: "fixed-day"; day: string }
    | { type: "last-day" }
    | { type: "last-working-day" };
  startsFrom: Date | null;
  endsOn: Date | null;
  isTaxable: boolean | null;
  taxProfileId: string | null;
  parentalLeavePolicy: {
    startsFrom: Date | null;
    afterPolicy: "full-pay" | "leave-job" | "last-rate";
    sequentialRules: Array<{
      amountType: "percentage" | "smp";
      percentage: number;
      chronoUnits: string;
      amountOfChronoUnits: number;
    }>;
  } | null;
  redundancy: {
    endDate: Date | null;
    paidOn: Date | null;
    weeks: number | null;
    payBasis: "full" | "statutory";
    hasNotice: boolean;
    noticeMonths: number | null;
    holidayDays: number | null;
  } | null;
  bonuses: Array<{
    date: Date | null;
    kind: "percentage" | "fixed";
    percentage: number | null;
    amount: number | null;
  }> | null;
};

export type CashflowRowItem = IncomesItem | OutgoingItem;

export type RowsConfig2<TItem> = {
  form: {
    getDefaultValue: FormData2;
    getInitialValue: (x: TItem) => FormData2;
    renderFields: (form: UseFormReturnType<FormData2>) => ReactNode;
    validate?: FormValidateInput<TItem>;
    onSubmitAddItem: (x: FormData2) => void;
    onSubmitEditItem: (x: FormData2, id: string) => void;
    onSubmitDeleteItem: (x: TItem) => void;
  };
  rows: Array<{
    id: string;
    text: string;
    getDisplayValue: (x: TItem) => JSX.Element | string | boolean | number;
    sortable?: boolean;
  }>;
};

export function GenericTable<TItem extends CashflowRowItem>({
  rowsConfig,
  items2,
}: {
  rowsConfig: RowsConfig2<TItem>;
  items2: TItem[];
}) {
  const [filterString, setFilterString] = useState<string | null>(null);
  const [isOpenAddModal, setIsOpenAddModal] = useState<boolean>(false);

  const [isOpenEditModal, setIsOpenEditModal] = useState<{
    item: TItem;
  } | null>(null);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<{
    item: TItem;
  } | null>(null);

  const [sortState, setSortState] = useState<{
    sortKey: string | null;
    sortOrder: "asc" | "desc";
  }>({
    sortKey: null,
    sortOrder: "asc",
  });

  const { notification } = useContext(AppContext);

  const buttonSize = "compact-md";

  const Th = ({
    id,
    sortable,
    title,
  }: {
    id: string;
    sortable?: boolean;
    title: string;
  }) => {
    const Title = (
      <Text fw={600} fz="sm">
        {title}
      </Text>
    );

    if (!sortable) {
      return <Table.Th id={id}>{Title}</Table.Th>;
    }

    const Icon =
      sortState.sortKey === id
        ? sortState.sortOrder === "asc"
          ? IconChevronUp
          : IconChevronDown
        : IconSelector;

    return (
      <Table.Th id={id}>
        <UnstyledButton
          onClick={() => {
            setSortState({
              sortKey: id,
              sortOrder: sortState.sortOrder === "asc" ? "desc" : "asc",
            });
          }}
        >
          <Group justify="space-between">
            {Title}
            <Center>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  return (
    <Box
      style={{
        width: "90%",
        maxWidth: "950px",
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <Group>
        <TextInput
          withAsterisk
          onChange={(event) => setFilterString(event.target.value)}
          placeholder="Search by any field"
          mb="md"
          style={{ flex: 1 }}
          leftSection={
            <IconSearch
              style={{ width: rem(16), height: rem(16) }}
              stroke={1.5}
            />
          }
        />
        <Group onClick={() => setIsOpenAddModal(true)}>
          <Button
            size="compact-md"
            style={{
              marginRight: "20px",
            }}
            w={50}
            h={40}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setIsOpenAddModal(true);
            }}
          >
            <IconPlus />
          </Button>
        </Group>
      </Group>

      <StyledTable captionSide="top" highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            {rowsConfig.rows.map((x) => (
              <Th key={x.id} id={x.id} title={x.text} sortable={x.sortable} />
            ))}
            <Th id="buttons" title="" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items2
            .filter((x) => {
              const stringified = `${x.amountPence} ${x.description} ${x.recurrence.kind}`;

              if (filterString) {
                return stringified.toLowerCase().includes(filterString.toLowerCase());
              }

              return true;
            })
            .sort((a, b) => {
              if (!sortState.sortKey) return 0;
              const row = rowsConfig.rows.find(
                (x) => x.id === sortState.sortKey,
              );
              if (!row || !row.sortable) return 0;

              const extractText = (node: ReactNode): string => {
                if (node == null || typeof node === "boolean") return "";
                if (typeof node === "string" || typeof node === "number") {
                  return String(node);
                }
                if (Array.isArray(node)) return node.map(extractText).join("");
                if (isValidElement(node))
                  return extractText(node.props.children);
                return "";
              };

              const toSortable = (
                value: ReturnType<typeof row.getDisplayValue>,
              ) => {
                if (typeof value === "number") return value;
                if (typeof value === "boolean") return value ? 1 : 0;
                if (typeof value === "string") return value.toLowerCase();
                if (isValidElement(value)) {
                  return extractText(value).toLowerCase();
                }
                return "";
              };

              const aVal = toSortable(row.getDisplayValue(a));
              const bVal = toSortable(row.getDisplayValue(b));

              if (typeof aVal === "number" && typeof bVal === "number") {
                return sortState.sortOrder === "asc"
                  ? aVal - bVal
                  : bVal - aVal;
              }

              const cmp = String(aVal).localeCompare(String(bVal));
              return sortState.sortOrder === "asc" ? cmp : -cmp;
            })
            .map((item) => (
              <Table.Tr id="row" key={item.id}>
                {rowsConfig.rows.map((x) => (
                  <Table.Td key={x.id} id={x.id}>
                    {x.getDisplayValue(item)}
                  </Table.Td>
                ))}

                <Table.Td id="buttons">
                  <div>
                    <Button
                      size={buttonSize}
                      onClick={() => {
                        setIsOpenEditModal({
                          item,
                        });
                      }}
                    >
                      <IconEdit />
                    </Button>
                    <Button
                      size={buttonSize}
                      onClick={() => setIsOpenDeleteModal({ item })}
                    >
                      <IconTrash />
                    </Button>
                  </div>
                </Table.Td>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </StyledTable>
      <Modal
        size="80%"
        opened={Boolean(isOpenAddModal)}
        onClose={() => setIsOpenAddModal(false)}
      >
        {
          <ItemForm2
            onSubmit={(x) => {
              rowsConfig.form.onSubmitAddItem(x);
              setIsOpenAddModal(false);
            }}
            item={undefined}
            config={rowsConfig.form}
          />
        }
      </Modal>

      <Modal
        size="80%"
        opened={Boolean(isOpenEditModal)}
        onClose={() => setIsOpenEditModal(null)} // null or false? be consistent
      >
        {
          <ItemForm2
            onSubmit={(x) => {
              rowsConfig.form.onSubmitEditItem(x, isOpenEditModal!.item.id);
              console.log("closing!");
              setIsOpenEditModal(null);
            }}
            item={isOpenEditModal?.item}
            config={rowsConfig.form}
          />
        }
      </Modal>

      <Modal
        size="80%"
        opened={Boolean(isOpenDeleteModal)}
        onClose={() => setIsOpenDeleteModal(null)}
      >
        <Box mx="auto">
          <Title order={2}>Delete</Title>
          <Stack>
            <Text>
              Are you sure you want to delete "
              {isOpenDeleteModal?.item.description}" ?
            </Text>
            <Group>
              <Button
                variant="primary"
                onClick={() => setIsOpenDeleteModal(null)}
              >
                No, cancel
              </Button>
              <Button
                color="red"
                onClick={(x) => {
                  rowsConfig.form.onSubmitDeleteItem(isOpenDeleteModal!.item);
                  setIsOpenDeleteModal(null);
                }}
              >
                Yes, delete
              </Button>
            </Group>
            {notification?.action === "delete" && (
              <Notification>{notification.message}</Notification>
            )}
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
}
